import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DEFAULT_VENUE_TIMEZONE } from "common-fe/venueTime";
import { useTranslation } from "i18n-fe";
import { formatPoints } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { buildMovementDiffRows } from "../util/contenderCompare";

// #2816 (scribe-resilience G3): the scoring tent's side-by-side view of two
// contender sheets for one judge seat (AC1/AC2), with the one-click switch
// action (AC3/AC4, server-side in apps/score-api/src/handlers/scoresheet.js
// switchAuthoritativeScoreSheet). Modeled on SheetAuditDialog.jsx's shape
// (same fmt() venue-local timestamp helper, same MUI Dialog/Table posture),
// but reads its data straight off the `sheets` prop RideEditor already has
// in state, no extra fetch, since listScoreSheets already returns full
// sheet objects including movementScores/provenance/authoritative/
// contenderOf.

const EMPTY_VALUE = "–"; // en dash placeholder for a missing value, not an em dash.

function fmt(at) {
  if (!at) return EMPTY_VALUE;
  try {
    // Same posture as SheetAuditDialog's fmt(): a genuine server-stamped
    // instant (ScoreSheet.provenance.capturedAt is `new Date().toISOString()`
    // on create, never an FVD Access stamp), so it renders venue-local
    // through the shared resolver rather than the viewer's browser timezone.
    return new Date(at).toLocaleString([], {
      timeZone: DEFAULT_VENUE_TIMEZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return at;
  }
}

function ProvenanceColumn({ sheet, isAuthoritative, t }) {
  const provenance = sheet.provenance ?? {};
  return (
    <Stack spacing={0.5} sx={{ mb: 1 }} data-testid={`contender-compare-column-${sheet.id}`}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color={isAuthoritative ? "success" : "default"}
          variant={isAuthoritative ? "filled" : "outlined"}
          data-testid={`contender-compare-status-chip-${sheet.id}`}
          label={t(
            isAuthoritative
              ? "contenderCompare.authoritativeChip"
              : "contenderCompare.contenderChip"
          )}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-origin-${sheet.id}`}>
        {t("contenderCompare.originLabel")} {provenance.origin || t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-author-${sheet.id}`}>
        {t("contenderCompare.authorLabel")} {provenance.author || t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-device-${sheet.id}`}>
        {t("contenderCompare.deviceLabel")} {provenance.device || t("contenderCompare.unknown")}
      </Typography>
      <Typography variant="body2" color="text.secondary" data-testid={`contender-compare-capturedat-${sheet.id}`}>
        {t("contenderCompare.capturedAtLabel")} {fmt(provenance.capturedAt)}
      </Typography>
    </Stack>
  );
}

function MarkCell({ mark }) {
  if (!mark) return <TableCell>{EMPTY_VALUE}</TableCell>;
  return <TableCell>{mark.score ?? EMPTY_VALUE}</TableCell>;
}

// #2986: buildMovementDiffRows can flag a row as `differs` purely on a
// non-score field (deductions/flagged/remarks/annotated*) that MarkCell
// never renders -- the row lit up warning-color with two identical-looking
// score cells and no visible reason. Names ONLY the non-score fields so the
// caption never duplicates what a genuine score difference already shows in
// the cells themselves.
const DIFF_FIELD_LABEL_KEYS = {
  deductions: "contenderCompare.diffField.deductions",
  flagged: "contenderCompare.diffField.flagged",
  remarks: "contenderCompare.diffField.remarks",
  annotatedDirective: "contenderCompare.diffField.annotatedDirective",
  annotatedDescription: "contenderCompare.diffField.annotatedDescription",
};

function nonScoreDiffFields(differingFields) {
  return (differingFields ?? []).filter((f) => f !== "score");
}

export default function ContenderCompareDialog({ open, onClose, judgeId, sheets, test, canSwitch, onSwitched }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [confirmSheetId, setConfirmSheetId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);

  const rows = buildMovementDiffRows(sheets);

  async function handleConfirmSwitch() {
    if (!confirmSheetId) return;
    try {
      setSwitching(true);
      setError(null);
      const updated = unwrap(
        await scoreApi.scoreSheets.switchAuthoritativeScoreSheet(confirmSheetId)
      );
      onSwitched?.(updated);
      setConfirmSheetId(null);
      onClose?.();
    } catch (err) {
      setError(err);
    } finally {
      setSwitching(false);
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("contenderCompare.title", { judgeId: judgeId || EMPTY_VALUE })}
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("contenderCompare.switchError")}
            </Alert>
          )}
          <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
            {(sheets ?? []).map((sheet, idx) => (
              <Box key={sheet.id} sx={{ flex: 1 }}>
                <ProvenanceColumn sheet={sheet} isAuthoritative={idx === 0} t={t} />
                {/* AC4: a principal without switch_authoritative_score_sheet
                    (a scribe) gets no button at all here, not a disabled
                    one: canSwitch is a token-roles check, never a UI-only
                    disable, so there is nothing to click even via devtools
                    that the server wouldn't also reject. */}
                {idx !== 0 && canSwitch && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setConfirmSheetId(sheet.id)}
                    data-testid="contender-compare-switch-button"
                  >
                    {t("contenderCompare.switchButton")}
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("contenderCompare.movementColumn")}</TableCell>
                  {(sheets ?? []).map((sheet, idx) => (
                    <TableCell key={sheet.id}>
                      {idx === 0
                        ? t("contenderCompare.authoritativeChip")
                        : t("contenderCompare.contenderChip")}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const nonScoreFields = nonScoreDiffFields(row.differingFields);
                  return (
                    <TableRow
                      key={row.num}
                      data-testid="contender-compare-row"
                      sx={
                        row.differs
                          ? { bgcolor: "warning.main", "& *": { color: "warning.contrastText" } }
                          : undefined
                      }
                    >
                      <TableCell>
                        {row.num}
                        {/* #2986 AC1: a row can be highlighted by a field this
                            table never renders per-cell (deductions/flagged/
                            remarks/annotated*) -- e.g. equal scores, different
                            remarks. Name it here so the highlight is never
                            unexplained. Score differences stay self-evident
                            in the cells and are deliberately not repeated. */}
                        {nonScoreFields.length > 0 && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="inherit"
                            data-testid="contender-compare-row-diff-hint"
                          >
                            {t("contenderCompare.differsInLabel")}{" "}
                            {nonScoreFields.map((f) => t(DIFF_FIELD_LABEL_KEYS[f])).join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                      {row.marks.map((mark, idx) => (
                        <MarkCell key={sheets?.[idx]?.id ?? idx} mark={mark} />
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {(sheets ?? []).map((sheet, idx) => (
              <span key={sheet.id}>
                {idx > 0 ? " · " : ""}
                {t("contenderCompare.totalLabel")} {formatPoints(sheet.totalPoints, test)} /{" "}
                {formatPoints(sheet.maxPoints, test)}
              </span>
            ))}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmSheetId)} onClose={() => setConfirmSheetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("contenderCompare.switchConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("contenderCompare.switchConfirmBody", { judgeId: judgeId || EMPTY_VALUE })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSheetId(null)} disabled={switching}>
            {t("contenderCompare.cancel")}
          </Button>
          <Button
            onClick={handleConfirmSwitch}
            loading={switching}
            variant="contained"
            data-testid="contender-compare-confirm-switch"
          >
            {t("contenderCompare.confirmSwitch")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
